import React, { useEffect, useState } from "react";
import {
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Visibility,
  Delete,
  Edit,
  Search,
  Add as AddIcon,
} from "@mui/icons-material";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useDelete from "../../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import EditCompanyMedicine from "../editCompanyMedicine/EditCompanyMedicine";
import { useNavigate } from "react-router-dom";

const ListCompanyMedicine = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  // Theme and Responsiveness
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Pagination States
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCompanies, setTotalCompanies] = useState(0);

  // Fetch Medicine Companies with Pagination
  const { data, refetch, isLoading, isError } = useFetchObjects(
    ["medicine-companies"],
    `medicine-companies/?search=${encodeURIComponent(
      searchQuery
    )}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  // Delete Handler
  const { handleDelete, ConfirmDialog } = useDelete(
    "medicine-companies",
    token
  );

  // Dialog States
  const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
  const [description, setDescription] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Handle Description Dialog
  const handleClickOpenDescriptionDialog = (desc) => {
    setDescription(desc);
    setOpenDescriptionDialog(true);
  };

  const handleCloseDescriptionDialog = () => {
    setOpenDescriptionDialog(false);
    setDescription("");
  };

  // Handle Edit Dialog
  const handleOpenEditDialog = (company) => {
    setSelectedCompany(company);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedCompany(null);
    refetch(); // Refresh data after editing
  };

  // Handle Pagination Page Change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Handle Rows Per Page Change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  // Update Total Companies from API Response
  useEffect(() => {
    if (data && typeof data.count === "number") {
      setTotalCompanies(data.count);
    }
  }, [data]);

  // Refetch data when dependencies change
  useEffect(() => {
    refetch();
  }, [refetch, searchQuery, page, rowsPerPage]);

  return (
    <Grid container mt={3}>
      <Grid item xs={12} md={12}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
              flexDirection: isSmallScreen ? "column" : "row",
              gap: 2,
            }}
          >
            <Typography variant="h5" fontWeight="bold" color="primary">
              {t("pharmacy.companyList")}
            </Typography>

            {/* Add New Button */}
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/pharmacy/medicine-company/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("pharmacy.addNew")}
            </Button>
          </Box>

          <Box mb={1}>
            {/* Search Bar */}
            <TextField
              placeholder={t("pharmacy.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: "300px" }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Loading, Error, or Data Display */}
          {isLoading ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Grid>
          ) : isError ? (
            <Typography color="error" textAlign="center">
              {t("pharmacy.failedToLoad")}
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
                    <TableRow>
                      <TableCell>{t("pharmacy.id")}</TableCell>
                      <TableCell>{t("pharmacy.companyName")}</TableCell>
                      <TableCell>{t("pharmacy.phoneNumber")}</TableCell>
                      <TableCell>{t("pharmacy.address")}</TableCell>
                      <TableCell>{t("pharmacy.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {data?.results?.length > 0 ? (
                      data.results.map((company) => (
                        <TableRow key={company.id}>
                          <TableCell>{company.id}</TableCell>
                          <TableCell>{company.name}</TableCell>
                          <TableCell>{company.phone}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() =>
                                handleClickOpenDescriptionDialog(
                                  company.address
                                )
                              }
                            >
                              <Visibility />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenEditDialog(company)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="secondary"
                              onClick={() => handleDelete(company.id)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="h6">
                            {t("pharmacy.noMedicineCompanyFound")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={4}
                mb={2}
                flexDirection={isSmallScreen ? "column" : "row"}
                gap={isSmallScreen ? 2 : 0}
              >
                {/* Rows Per Page Selector */}
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" mr={2}>
                    {t("pharmacy.rowsPerPage")}:
                  </Typography>
                  <FormControl variant="outlined" size="small">
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      sx={{
                        width: 80,
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      }}
                    >
                      {[10, 25, 50, 100, 500, 1000].map((rows) => (
                        <MenuItem key={rows} value={rows}>
                          {rows}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Pagination Component */}
                <Pagination
                  count={Math.ceil(totalCompanies / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  variant="outlined"
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          )}

          {/* Confirm Delete Dialog */}
          <ConfirmDialog />

          {/* Description Dialog */}
          <Dialog
            open={openDescriptionDialog}
            onClose={handleCloseDescriptionDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{t("pharmacy.companyAddress")}</DialogTitle>
            <DialogContent>
              <Typography variant="body1">{description}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDescriptionDialog} color="primary">
                {t("pharmacy.close")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Company Medicine Dialog */}
          {selectedCompany && (
            <EditCompanyMedicine
              open={openEditDialog}
              onClose={handleCloseEditDialog}
              company={selectedCompany}
              token={token}
            />
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ListCompanyMedicine;
